import React, { FormEvent } from 'react'
import { graphql, withApollo } from 'react-apollo'
import { useMediaQuery } from 'react-responsive'
import { Link, Redirect, withRouter } from 'react-router-dom'

import { AcornsIcon } from '@acorns/icons'
import {
  Button,
  ConfirmationModal,
  ModalActionTypes,
  PrivateInputV3,
  Spinner,
  TextInputV3,
  Title,
} from '@acorns/web-components'
import { useFeature, withToggles } from '@acorns/web-toggles-react'
import { GenericAnalyticsClient, withAnalyticsClient } from '@acorns/web-utils'
import { withFormik } from 'formik'
import {
  compose,
  lifecycle,
  withHandlers,
  withProps,
  withState,
} from 'recompose'
import styled from 'styled-components'

import AcornsLogo from 'assets/icons/acorns-logo.svg'
import AlertBadge from 'assets/icons/alert-badge.png'
import acornsTitle from 'assets/images/acorns-title.svg'
import Alert from 'assets/images/alert.png'
import Layout from 'components/Layout'
import OAuthPartnerContent, {
  KNOWN_OAUTH_PARTNERS,
} from 'components/OAuthPartnerContent'
import { ThirdPartyLogins } from 'components/ThirdPartyLogins'
import {
  OAuthFlowOAuthEventOccurredType,
  trackOAuthFlowOAuthEventOccurredPayload,
} from 'generated/trackingEvents'
import theme from 'theme'
import { withEnv } from 'utils/environment'
import {
  emailRedesignWeb,
  maintenanceScreenWeb,
  oakRedirect,
  oakRedirectDeeplinks,
  trustThisDeviceWeb,
} from 'utils/experiments/features'
import getWindow from 'utils/get-window'
import { extractOAuthParams } from 'utils/oAuth/extractOAuthParams'
import { logInViewed } from 'utils/segment'
import { OAuthPartnerConfig } from 'utils/shared-types'
import { FormContainer, InputContainer } from 'utils/styled-components'
import { LOGIN_FORM, SIGN_UP_INSTEAD_LINK } from 'utils/testing/testIds'
import withAnalyticsIdentity from 'utils/with-analytics-identity'

import { registrationUrlRedirect } from '../../utils/login-utils'
import { withAuthenticateErrorHandler } from './errors'
import AuthenticateExceptionHandler from './exceptions'
import {
  Values,
  handleFormSubmit,
  mapPropsToValues,
  resolveRedirect,
  validationSchema,
} from './form'
import Authenticate from './queries/authenticate.gql'
import Texts from './texts'

type CustomTextValues = {
  login: string
  signupDescription?: string
  signUpCta: string
}

type OAuthPartnerParams = {
  oAuthPartnerParams?: OAuthPartnerConfig
  client_id?: string
}

export type Props = {
  children: any
  copy: string
  customTexts: CustomTextValues
  values: Values
  isSubmitting: boolean
  isValid: boolean
  openRegistration: () => void
  isModalOpen: boolean
  isValidationModalOpen: boolean
  isUnableToVerifyModalOpen: boolean
  setIsUnableToVerifyModalOpen: (value: boolean) => void
  closeModal: () => void
  handleChange: (event: FormEvent<HTMLInputElement>) => void
  closeErrorModal: () => void
  handleSubmit: () => void
  handleBlur: (event: FormEvent<HTMLInputElement>) => void
  giftcardPage: boolean
  env: any
  exception: string
  analytics: GenericAnalyticsClient
  timeOnLogInView: number
  setTimeOnLogInView: (time: number) => void
  trustThisDeviceWebIsEnabled: boolean
  isOAuth?: boolean
  oAuthPartnerParams?: OAuthPartnerParams
  setOAuthPartnerParams: (params: OAuthPartnerParams) => void
  chromeExtensionPartnerFlow?: boolean
  setChromeExtensionPartnerFlow?: (value: boolean) => void
}

const GiftcardContainer = styled.div`
  display: flex;
  max-width: 340px;
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.ivory};
  border-radius: 10px;
  margin: auto;
  padding: 20px;
  margin-bottom: 30px;
`

const GiftcardIconContainer = styled.div`
  margin-right: 12px;
`
const GiftcardContentContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  text-align: left;
`

const SignupLink = styled(Link)`
  font-size: 14px;
  font-weight: 600;
  line-height: 22px;
  cursor: pointer;
  color: ${theme.colors.green};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`

const SignupCopy = styled.div`
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  color: ${theme.colors.slate};
  margin-bottom: 5px;
  margin-top: -2px;
`

const Form = styled.form`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`

const StyledDisclosureRight = styled(AcornsIcon)`
  margin-left: 8px;

  path {
    &[fill] {
      fill: ${(props) => props.theme.colors.green};
    }
  }
`

const withRouteSpecificText = withProps(() => {
  const { pathname } = window.location
  const customTexts = Texts[pathname] || Texts.default
  return { customTexts }
})

const GiftcardSignupSection = ({ customTexts, openRegistration }) => (
  <GiftcardContainer>
    <GiftcardIconContainer>
      <AcornsIcon icon={AcornsIcon.Icon.UtilityGift} height={20} width={20} />
    </GiftcardIconContainer>
    <GiftcardContentContainer>
      <SignupCopy>{customTexts.signupDescription}</SignupCopy>
      <SignupLink
        data-testid={SIGN_UP_INSTEAD_LINK}
        to="/redeem"
        onClick={openRegistration}
      >
        {customTexts.signUpCta}
        <StyledDisclosureRight
          height={10}
          width={10}
          icon={AcornsIcon.Icon.DisclosureRight}
        />
      </SignupLink>
    </GiftcardContentContainer>
  </GiftcardContainer>
)

export const enhance = compose<any, any>(
  withAnalyticsClient,
  withAnalyticsIdentity,
  withEnv,
  withRouter,
  withApollo,
  withToggles,
  graphql(Authenticate, { name: 'login' }),
  withState('isModalOpen', 'setIsModalOpen', false),
  withState('exception', 'setException', ''),
  withState('isValidationModalOpen', 'setIsValidationModalOpen', false),
  withState('isUnableToVerifyModalOpen', 'setIsUnableToVerifyModalOpen', false),
  withState('timeOnLogInView', 'setTimeOnLogInView', null),
  withState('oAuthPartnerParams', 'setOAuthPartnerParams', {}),
  withState(
    'chromeExtensionPartnerFlow',
    'setChromeExtensionPartnerFlow',
    false,
  ),

  withProps(() => {
    const [trustThisDeviceWebIsEnabled] = useFeature(trustThisDeviceWeb)
    return { trustThisDeviceWebIsEnabled }
  }),
  withFormik({
    validationSchema,
    mapPropsToValues,
    handleSubmit: handleFormSubmit,
  }),
  withRouteSpecificText,
  withProps(() => ({
    giftcardPage: window.location.pathname === '/redeem',
  })),
  withHandlers({
    openRegistration:
      ({ env }) =>
      () => {
        const registrationUrl = env.get('registrationUrl')
        const windowLocation = window.location
        const RegistrationUrlArgs = {
          registrationUrl,
          windowLocation,
        }
        window.location.href = registrationUrlRedirect(RegistrationUrlArgs)
      },
    closeModal:
      ({
        setIsModalOpen,
        setIsValidationModalOpen,
        setIsUnableToVerifyModalOpen,
      }) =>
      () => {
        setIsModalOpen(false)
        setIsValidationModalOpen(false)
        setIsUnableToVerifyModalOpen(false)
      },
  }),
  lifecycle<Props, {}>({
    componentDidMount() {
      this.props.analytics.track('Screen Viewed', logInViewed)
      this.props.setTimeOnLogInView(Date.now())

      const { chromeExtensionPartnerFlow, oAuthPartnerParams } =
        extractOAuthParams(window.location)

      if (this.props.isOAuth) {
        this.props.analytics.track(
          ...trackOAuthFlowOAuthEventOccurredPayload({
            screen: 'logIn',
            type: OAuthFlowOAuthEventOccurredType.Login_page_landed,
            oauthAppId: oAuthPartnerParams?.app_id,
            oauthAppName: oAuthPartnerParams?.app_name,
            oauthAppClientId: oAuthPartnerParams?.app_client_name,
            oauthAppClientName: oAuthPartnerParams?.app_client_name,
          }),
        )
      }

      if (
        oAuthPartnerParams.hasOwnProperty('app_client_id') &&
        !oAuthPartnerParams.hasOwnProperty('app_client_name')
      ) {
        throw new Error('app_client_name is missing')
      }

      if (chromeExtensionPartnerFlow) {
        this.props.setChromeExtensionPartnerFlow(true)
      }

      this.props.setOAuthPartnerParams(oAuthPartnerParams)
    },
  }),
  withAuthenticateErrorHandler,
)

const LoginTitle = styled(Title)`
  font-weight: 600;
  font-size: 22px;
  line-height: 30px;
  text-align: center;
  color: #000000;
  margin-bottom: 30px;
`

const LoginTitleV2 = styled(Title)`
  font-weight: 600;
  font-size: 30px;
  text-align: center;
  margin-bottom: 10px;
`

const Email = styled(TextInputV3)`
  display: block;
  max-width: 340px;
  margin-bottom: 20px;
  width: 100%;
`

const Password = styled(PrivateInputV3)<{ emailRedesignWebIsEnabled: boolean }>`
  display: block;
  max-width: 340px;
  margin-bottom: ${({ emailRedesignWebIsEnabled }) =>
    emailRedesignWebIsEnabled ? '15px' : '30px'};
  width: 100%;
`

const ForgotPass = styled(Link)`
  font-size: 14px;
  font-weight: 600;
  color: ${theme.colors.stone};
  line-height: 22px;
  letter-spacing: 0px;
  text-decoration: none;
  text-align: center;
`

const ForgotPassV2 = styled(Link)`
  font-size: 12px;
  font-weight: 600;
  color: ${theme.colors.stone};
  line-height: 22px;
  letter-spacing: 0px;
  text-decoration: none;
  align-self: flex-start;
  padding: 0 17px;
  margin-bottom: 40px;
`

const LoginButton = styled(Button)<{ emailRedesignWebIsEnabled: boolean }>`
  margin: auto;
  max-width: 340px;
  width: 100%;
  margin-bottom: ${({ emailRedesignWebIsEnabled }) =>
    emailRedesignWebIsEnabled ? '0' : '20px'};
`

const ActionsContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: center;
`

const SignupText = styled.div`
  margin-top: 92px;
  font-size: 14px;
  font-weight: 600;
  text-align: center;
`

const SignupCta = styled.a`
  font-size: 14px;
  font-weight: 600;
  color: ${theme.colors.green};
  cursor: pointer;
`

const LoginSubtitle = styled.div`
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  text-align: center;
  margin-bottom: 40px;
`

interface EmailRedesignWebVariables {
  loginPageTitle: string
  loginPageSubtitle: string
  loginPageSignupText: string
  loginPageSignupCta: string
  loginPageUnableToVerifyTitle: string
  loginPageUnableToVerifyMessage: string
  loginPageUnableToVerifyPrimaryCta: string
  loginPageUnableToVerifySecondaryCta: string
}

export const Login = ({
  closeModal,
  customTexts,
  giftcardPage,
  env,
  handleChange,
  handleSubmit,
  isModalOpen,
  isSubmitting,
  isValid,
  isValidationModalOpen,
  isUnableToVerifyModalOpen,
  openRegistration,
  values,
  exception,
  isOAuth,
  oAuthPartnerParams,
  chromeExtensionPartnerFlow,
}: Props) => {
  const [maintenanceScreenWebIsEnabled] = useFeature(maintenanceScreenWeb)
  const [emailRedesignWebIsEnabled, emailRedesignWebIsEnabledVariables] =
    useFeature<EmailRedesignWebVariables>(emailRedesignWeb)
  const [oakRedirectIsEnabled] = useFeature(oakRedirect)
  const [oakRedirectDeeplinksIsEnabled] = useFeature(oakRedirectDeeplinks)

  const isMobile = useMediaQuery({
    maxWidth: `${theme.breakpoints.sm}px`,
  })

  const showOAuthPartnerContent = (
    isOauth: boolean,
    params: OAuthPartnerParams,
  ) => {
    if (isOauth) {
      const isPartner =
        params.page_type && KNOWN_OAUTH_PARTNERS.includes(params.page_type)
      const isChromeExtensionPartner =
        params.client_id && KNOWN_OAUTH_PARTNERS.includes(params.client_id)
      return isPartner || isChromeExtensionPartner
    }
    return false
  }

  const title = emailRedesignWebIsEnabled ? (
    <>
      <LoginTitleV2>
        {emailRedesignWebIsEnabledVariables.loginPageTitle}
      </LoginTitleV2>
      <LoginSubtitle>
        {emailRedesignWebIsEnabledVariables.loginPageSubtitle}
      </LoginSubtitle>
    </>
  ) : (
    <LoginTitle>Log in to Acorns</LoginTitle>
  )

  if (maintenanceScreenWebIsEnabled) {
    return <Redirect to="/maintenance" />
  }

  const redirect = resolveRedirect(
    env.get('defaultRedirect'),
    getWindow().document.referrer,
  )

  const hasDefaultRedirect = redirect === `${env.get('defaultRedirect')}/`

  if (oakRedirectIsEnabled && hasDefaultRedirect && !isOAuth) {
    window.location.href = process.env.OAK_SIGN_IN_URL
    return null
  }

  if (oakRedirectDeeplinksIsEnabled && !hasDefaultRedirect && !isOAuth) {
    window.location.href =
      process.env.OAK_SIGN_IN_URL + '?continue=' + encodeURIComponent(redirect)
    return null
  }

  return (
    <Layout
      title={
        emailRedesignWebIsEnabled && isMobile ? (
          <img src={AcornsLogo} height={40} width={40} />
        ) : (
          <img src={acornsTitle} width={97} height={26} />
        )
      }
      showBack={false}
      isOAuth={isOAuth}
    >
      <FormContainer>
        <Form
          noValidate={true}
          data-id="login-form"
          data-testid={LOGIN_FORM}
          giftcardPage={giftcardPage}
          onSubmit={handleSubmit}
          name="login-form"
        >
          {showOAuthPartnerContent(isOAuth, oAuthPartnerParams) ? (
            <OAuthPartnerContent
              oAuthPartnerParams={oAuthPartnerParams}
              chromeExtensionPartnerFlow={chromeExtensionPartnerFlow}
            />
          ) : (
            title
          )}
          <InputContainer>
            <Email
              label={{ value: 'Email' }}
              inputProps={{
                autoFocus: true,
                name: 'email',
                placeholder: 'Enter your email',
                onChange: handleChange,
                value: values.email,
                type: 'email',
              }}
            />
          </InputContainer>

          <InputContainer>
            <Password
              emailRedesignWebIsEnabled={emailRedesignWebIsEnabled}
              label={{ value: 'Password' }}
              inputProps={{
                name: 'password',
                placeholder: 'Enter your password',
                value: values.password,
                onChange: handleChange,
                type: 'password',
              }}
            />
          </InputContainer>
          {emailRedesignWebIsEnabled && (
            <ForgotPassV2 to="/forgot-password">Forgot Password?</ForgotPassV2>
          )}

          {giftcardPage && (
            <GiftcardSignupSection
              customTexts={customTexts}
              openRegistration={openRegistration}
            />
          )}
          <ActionsContainer>
            <LoginButton
              emailRedesignWebIsEnabled={emailRedesignWebIsEnabled}
              disabled={isSubmitting || !isValid}
              onPress={handleSubmit}
              buttonType="submit"
              name="login-button"
            >
              {!isSubmitting ? (
                customTexts.login
              ) : (
                <Spinner height={20} width={20} color={'white'} />
              )}
            </LoginButton>
            {!emailRedesignWebIsEnabled && (
              <ForgotPass to="/forgot-password">Forgot Password?</ForgotPass>
            )}
          </ActionsContainer>
        </Form>
        <ThirdPartyLogins isOAuth={isOAuth} />
        {emailRedesignWebIsEnabled && (
          <SignupText>
            {emailRedesignWebIsEnabledVariables.loginPageSignupText}{' '}
            <SignupCta onClick={openRegistration}>
              {emailRedesignWebIsEnabledVariables.loginPageSignupCta}
            </SignupCta>
          </SignupText>
        )}
      </FormContainer>
      <ConfirmationModal
        isOpen={isValidationModalOpen}
        actions={[
          {
            label: 'OK',
            type: ModalActionTypes.primary,
            handler: closeModal,
          },
        ]}
        title="Invalid Email Address"
        message="Sorry, it doesn't look like the email address is valid. Please try again."
        image={AlertBadge}
      />
      {emailRedesignWebIsEnabled && (
        <ConfirmationModal
          isOpen={isUnableToVerifyModalOpen}
          actions={[
            {
              label:
                emailRedesignWebIsEnabledVariables.loginPageUnableToVerifySecondaryCta,
              type: ModalActionTypes.secondary,
              handler: closeModal,
            },
            {
              label:
                emailRedesignWebIsEnabledVariables.loginPageUnableToVerifyPrimaryCta,
              type: ModalActionTypes.primary,
              handler: openRegistration,
            },
          ]}
          title={
            emailRedesignWebIsEnabledVariables.loginPageUnableToVerifyTitle
          }
          message={
            emailRedesignWebIsEnabledVariables.loginPageUnableToVerifyMessage
          }
          image={Alert}
        />
      )}
      <AuthenticateExceptionHandler
        exception={exception}
        handleCloseExceptionModal={closeModal}
        isExceptionModalOpen={isModalOpen}
        env={env}
      />
    </Layout>
  )
}

export default enhance(Login)
